import { reportError } from "@/utils/reportError";
import { isAbortError } from "@/utils/errors";
import { supabase } from "@/utils/supabase";
import { getCached, setCached, clearAllCached } from "@/utils/cache";
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

export type Gender = 'femme' | 'homme' | 'autre' | 'prefere_ne_pas_dire';

export interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  age: number | null;
  gender: Gender | null;
  company: string | null;
  companyId: string | null;
  postalCode: string;
  phone: string | null;
  avatarUrl: string | null;
  termsAccepted: boolean;
  displayMainRanking: boolean | null;
  isAdmin: boolean;
}

interface ProfileContextType {
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
  updateProfile: (updates: Partial<Omit<UserProfile, "id" | "email" | "termsAccepted">>) => Promise<{ success: boolean; error?: string }>;
  refreshProfile: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

/**
 * Relie une entreprise saisie à l'inscription à sa ligne `companies`.
 *
 * À l'inscription il n'y a pas encore de session : la policy RLS interdit
 * d'insérer dans `companies`, donc create-account.tsx ne peut enregistrer que
 * le NOM en texte libre (métadonnée `company`). `company_id` reste nul.
 *
 * Or le classement par entreprise repose entièrement sur `company_id`
 * (useRanking.ts) : sans lui, l'utilisateur voit « Rejoins une entreprise »
 * alors qu'il en a choisi une à l'inscription, et n'apparaît dans aucun
 * classement d'entreprise. Sur un challenge déployé PAR les entreprises,
 * c'est la fonctionnalité principale qui manquait.
 *
 * On répare donc dès qu'une session existe, une seule fois par compte : la
 * ligne est retrouvée (insensible à la casse) ou créée, puis liée.
 * Silencieux par construction — un échec (hors ligne, course) laisse
 * simplement le lien à faire au prochain lancement.
 */
async function linkCompanyIfNeeded(
  userId: string,
  companyName: string
): Promise<string | null> {
  try {
    const trimmed = companyName.trim();
    if (!trimmed) return null;

    const { data: existing } = await supabase
      .from("companies")
      .select("id")
      .ilike("name", trimmed)
      .maybeSingle();

    let companyId = existing?.id ?? null;

    if (!companyId) {
      const { data: created, error: insertError } = await supabase
        .from("companies")
        .insert({ name: trimmed })
        .select("id")
        .single();
      if (insertError) {
        // Course : un autre client vient d'insérer le même nom et l'index
        // unique sur lower(name) rejette. On relit l'existante.
        const { data: retry } = await supabase
          .from("companies")
          .select("id")
          .ilike("name", trimmed)
          .maybeSingle();
        companyId = retry?.id ?? null;
      } else {
        companyId = created?.id ?? null;
      }
    }

    if (!companyId) return null;

    const { error: updateError } = await supabase
      .from("profiles")
      .update({ company_id: companyId })
      .eq("id", userId);
    if (updateError) return null;

    return companyId;
  } catch {
    return null;
  }
}

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    try {
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        setProfile(null);
        setLoading(false);
        return;
      }

      // Stale-while-revalidate: hydrate from cache immediately if available
      // so the UI has data on cold start, then refetch in the background.
      const cacheKey = `profile:${user.id}`;
      const cached = await getCached<UserProfile>(cacheKey);
      if (cached) {
        setProfile(cached);
        setLoading(false);
      } else {
        setLoading(true);
      }

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (profileError) {
        if (profileError.code === "PGRST116") {
          // Session valide mais AUCUNE ligne profiles : état incohérent, et le
          // serveur a bien répondu (ce n'est pas un souci réseau). Arrive quand
          // le compte a été supprimé ailleurs, ou masqué par le soft-delete —
          // le JWT reste pourtant valide jusqu'à son expiration, une heure.
          // Sans cette déconnexion, l'écran Profil restait sur un chargement
          // infini, sans aucune issue pour l'utilisateur.
          setProfile(null);
          setLoading(false);
          await supabase.auth.signOut();
          return;
        }
        throw profileError;
      }

      const userProfile: UserProfile = {
        id: profileData.id,
        email: profileData.email,
        firstName: profileData.first_name || "",
        lastName: profileData.last_name || "",
        age: profileData.age ?? null,
        gender: (profileData.gender ?? null) as Gender | null,
        company: profileData.company || null,
        companyId: profileData.company_id || null,
        postalCode: profileData.postal_code || "",
        phone: profileData.phone || null,
        avatarUrl: profileData.avatar_url || null,
        termsAccepted: profileData.terms_accepted || false,
        displayMainRanking: profileData.display_main_ranking,
        isAdmin: profileData.is_admin === true,
      };

      setProfile(userProfile);
      setCached(`profile:${user.id}`, userProfile);

      // Entreprise choisie à l'inscription mais jamais liée (cf.
      // linkCompanyIfNeeded) : on rattrape maintenant qu'une session existe.
      if (userProfile.company && !userProfile.companyId) {
        const linkedId = await linkCompanyIfNeeded(user.id, userProfile.company);
        if (linkedId) {
          const linked = { ...userProfile, companyId: linkedId };
          setProfile(linked);
          setCached(`profile:${user.id}`, linked);
        }
      }
    } catch (err: any) {
      // Aborted request (app backgrounded, screen unmounted…): transient,
      // keep whatever profile we already have instead of wiping it.
      if (isAbortError(err)) return;
      reportError(err);
      setError(err.message);
      // Toute erreur ici est transitoire (réseau, timeout, 5xx) : on GARDE le
      // profil courant (souvent fraîchement hydraté du cache) au lieu de le
      // vider — sinon l'écran Profil reste bloqué sur le loader hors-ligne.
      // Les cas « pas de profil » sont gérés plus haut (session absente,
      // PGRST116) et au SIGNED_OUT.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        fetchProfile();
      } else if (event === 'SIGNED_OUT') {
        setProfile(null);
        clearAllCached();
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const updateProfile = async (
    updates: Partial<Omit<UserProfile, "id" | "email" | "termsAccepted">>
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error("No user logged in");

      const dbUpdates: any = {};
      if (updates.firstName !== undefined) dbUpdates.first_name = updates.firstName;
      if (updates.lastName !== undefined) dbUpdates.last_name = updates.lastName;
      if (updates.age !== undefined) dbUpdates.age = updates.age;
      if (updates.gender !== undefined) dbUpdates.gender = updates.gender;
      if (updates.company !== undefined) dbUpdates.company = updates.company;
      if (updates.companyId !== undefined) dbUpdates.company_id = updates.companyId;
      if (updates.postalCode !== undefined) dbUpdates.postal_code = updates.postalCode;
      if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
      if (updates.avatarUrl !== undefined) dbUpdates.avatar_url = updates.avatarUrl;
      if (updates.displayMainRanking !== undefined) dbUpdates.display_main_ranking = updates.displayMainRanking;

      const { error: updateError } = await supabase
        .from("profiles")
        .update(dbUpdates)
        .eq("id", user.id);

      if (updateError) throw updateError;

      // Immediately update local state for instant UI feedback
      setProfile(prev => {
        const next = prev ? { ...prev, ...updates } : null;
        if (next) setCached(`profile:${user.id}`, next);
        return next;
      });

      return { success: true };
    } catch (err: any) {
      reportError(err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  return (
    <ProfileContext.Provider value={{ profile, loading, error, isAdmin: profile?.isAdmin ?? false, updateProfile, refreshProfile: fetchProfile }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return context;
}
